// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

/**
 * Game Mechanics Configuration (Aggregator)
 * 
 * This file imports and re-exports all game configuration modules.
 * Individual configs are now split into separate files for better organization.
 * 
 * Import structure maintained for backward compatibility:
 * - GameConfig.credits.* → CoreGameConfig.economy
 * - GameConfig.fuel.* → CoreGameConfig.fuel
 * - GameConfig.warp.* → CoreGameConfig.warp
 * - GameConfig.ui.* → CoreGameConfig.uiConfig
 * - GameConfig.difficulty.* → CoreGameConfig.gameSettings.difficulty
 * - GameConfig.specialUpgrades.* → CoreGameConfig.gameSettings.specialUpgrades
 * - GameConfig.shopInventory.* → CoreGameConfig.gameSettings.shopInventory
 * - GameConfig.cheats.* → CoreGameConfig.gameSettings.cheats
 * - GameConfig.events.* → EventsConfig
 * - GameConfig.combat.* → CombatConfig
 * - GameConfig.upgrades.* → UpgradesConfig
 * - GameConfig.weapons.* → WeaponsConfig
 * - GameConfig.shipHealth.* → ShipHealthConfig
 * - GameConfig.scanning.* → ExplorationConfig.scanning
 * - GameConfig.exploration.* → ExplorationConfig.exploration
 */

import { economy, fuel, warp, uiConfig, gameSettingsConfig } from './CoreGameConfig';
import { eventsConfig } from './EventsConfig';
import { combatConfig } from './CombatConfig';
import { upgradesConfig } from './UpgradesConfig';
import { weaponsConfig } from './WeaponsConfig';
import { shipHealthConfig } from './ShipHealthConfig';
import { explorationConfig } from './ExplorationConfig';
import { crewRelationshipConfig } from './CrewRelationshipConfig';

// Re-export as single gameMechanics object for backward compatibility
export const gameMechanics = {
  credits: economy,
  fuel: fuel,
  warp: warp,
  events: eventsConfig,
  combat: combatConfig,
  upgrades: upgradesConfig,
  weapons: weaponsConfig,
  shipHealth: shipHealthConfig,
  scanning: explorationConfig.scanning,
  exploration: explorationConfig, // Full exploration config including locations and getLocationsForThreat
  crewRelationships: crewRelationshipConfig,
  ui: uiConfig,
  difficulty: gameSettingsConfig.difficulty,
  specialUpgrades: gameSettingsConfig.specialUpgrades,
  shopInventory: gameSettingsConfig.shopInventory,
  cheats: gameSettingsConfig.cheats
};

// Also export individual configs for direct imports (optional, for future use)
export {
  economy,
  fuel,
  warp,
  eventsConfig,
  combatConfig,
  upgradesConfig,
  weaponsConfig,
  shipHealthConfig,
  explorationConfig,
  uiConfig,
  gameSettingsConfig
};
